import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useBookmarkedTenders, useBookmarkedAgencies } from '@/hooks/useWatchlist'
import { useBookmarkedTags } from '@/hooks/useTags'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { PROVINCE_NAMES } from '@/lib/constants'

export function WatchlistPage() {
  const [activeTab, setActiveTab] = useState('tenders')

  const { data: tenders, isLoading: tendersLoading } = useBookmarkedTenders()
  const { data: agencies, isLoading: agenciesLoading } = useBookmarkedAgencies()
  const { data: tags, isLoading: tagsLoading } = useBookmarkedTags()

  const totalBookmarks =
    (tenders?.length || 0) +
    (agencies?.length || 0) +
    (tags?.length || 0)

  return (
    <div>
      <PageHeader
        title="รายการที่บันทึก"
        description={`${totalBookmarks} รายการที่บันทึกไว้`}
      />

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="tenders">
            ประกาศ
            {tenders && tenders.length > 0 && (
              <Badge size="sm" className="ml-2">{tenders.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="agencies">
            หน่วยงาน
            {agencies && agencies.length > 0 && (
              <Badge size="sm" className="ml-2">{agencies.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tags">
            แท็ก
            {tags && tags.length > 0 && (
              <Badge size="sm" className="ml-2">{tags.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tenders tab */}
        <TabsContent value="tenders">
          {tendersLoading ? (
            <LoadingSkeleton />
          ) : !tenders || tenders.length === 0 ? (
            <EmptyState
              icon="solar:document-add-line-duotone"
              title="ยังไม่มีประกาศที่บันทึก"
              description="ประกาศที่คุณบันทึกจะแสดงที่นี่"
              actionLabel="เรียกดูประกาศ"
              actionHref="/tenders"
            />
          ) : (
            <div className="flex flex-col gap-3">
              {tenders.filter((item): item is { bookmark: typeof item.bookmark; tender: NonNullable<typeof item.tender> } => item.tender !== undefined).map(({ bookmark, tender }) => (
                <Link key={bookmark.id} to={`/tenders/${tender.id}`}>
                  <Card hover>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-medium text-slate-900 line-clamp-2">
                          {tender.project_name || tender.title_th || 'ประกาศไม่มีชื่อ'}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          {tender.project_id && (
                            <span>รหัส: {tender.project_id}</span>
                          )}
                          {tender.updated ? (
                            <DateDisplay date={tender.updated} format="relative" />
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      </div>
                      <Icon icon="solar:bookmark-bold" className="w-5 h-5 text-blue-600" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Agencies tab */}
        <TabsContent value="agencies">
          {agenciesLoading ? (
            <LoadingSkeleton />
          ) : !agencies || agencies.length === 0 ? (
            <EmptyState
              icon="solar:buildings-line-duotone"
              title="ยังไม่มีหน่วยงานที่บันทึก"
              description="หน่วยงานที่คุณติดตามจะแสดงที่นี่"
              actionLabel="เรียกดูหน่วยงาน"
              actionHref="/agencies"
            />
          ) : (
            <div className="flex flex-col gap-3">
              {agencies.filter((item): item is { bookmark: typeof item.bookmark; agency: NonNullable<typeof item.agency> } => item.agency !== undefined).map(({ bookmark, agency }) => (
                <Link key={bookmark.id} to={`/agencies/${agency.id}`}>
                  <Card hover>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                        <Icon icon="solar:buildings-line-duotone" className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-medium text-slate-900 truncate">
                          {agency.name}
                        </p>
                        {agency.province_code && (
                          <p className="text-sm text-slate-500">
                            {PROVINCE_NAMES[agency.province_code] || agency.province_code}
                          </p>
                        )}
                      </div>
                      <Icon icon="solar:bookmark-bold" className="w-5 h-5 text-blue-600" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tags tab */}
        <TabsContent value="tags">
          {tagsLoading ? (
            <LoadingSkeleton />
          ) : !tags || tags.length === 0 ? (
            <EmptyState
              icon="solar:tag-line-duotone"
              title="ยังไม่มีแท็กที่บันทึก"
              description="แท็กที่คุณติดตามจะแสดงที่นี่"
              actionLabel="เรียกดูแท็ก"
              actionHref="/tags"
            />
          ) : (
            <div className="flex flex-wrap gap-3">
              {tags.filter((tag): tag is NonNullable<typeof tag> => tag !== undefined).map((tag) => (
                <Link
                  key={tag.id}
                  to={`/tags/${tag.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-full hover:bg-blue-100 transition-colors font-display"
                >
                  <Icon icon="solar:bookmark-bold" className="w-4 h-4 text-blue-600" />
                  <span>{tag.name}</span>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-48 bg-slate-200 rounded" />
              <div className="h-3 w-32 bg-slate-200 rounded" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  actionHref,
}: {
  icon: string
  title: string
  description: string
  actionLabel: string
  actionHref: string
}) {
  return (
    <Card className="p-8 text-center">
      <Icon icon={icon} className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
      <Link to={actionHref}>
        <Button variant="outline" size="sm" className="mt-4">
          {actionLabel}
        </Button>
      </Link>
    </Card>
  )
}
